/**
 * The per-site configuration, `hotpot-cms.yaml` at the root of each site repo.
 *
 * It is data (the dashboard reads it through GitHub), written in snake_case like Sveltia's config so that
 * studio developers can move between the two. It has two halves:
 *  - the content model: collections, singletons (files), fields, media folders, i18n (must match the site's
 *    `src/content.config.ts`);
 *  - the client-facing structure: navigation groups and order, labels, help text, hidden/advanced fields and
 *    what the start screen shows.
 */

export const CONFIG_FILE = 'hotpot-cms.yaml';
export const CONFIG_VERSION = 1;

export interface SiteConfig {
  version: 1;
  site: SiteInfo;
  /** Default media folder for collections and fields that don't set their own. */
  media: MediaConfig;
  /** Image processing in the browser before upload. */
  uploads?: UploadConfig;
  slug?: SlugConfig;
  i18n?: I18nConfig;
  /** Sidebar groups. Items are collection names, or `collection/file` for one singleton. */
  navigation?: NavGroup[];
  /** Items shown under "Global settings" at the bottom of the sidebar (same format as navigation items). */
  settings?: string[];
  start?: StartScreen;
  collections: Collection[];
}

export interface SiteInfo {
  name: string;
  /** Live site, used for "View on site" links. */
  url?: string;
  /** owner/repo on GitHub */
  repo?: string;
  branch?: string;
  /** Language of the client-facing labels in this file (`cs`, `en`); the dashboard's own UI follows it. */
  ui_language?: string;
  logo?: string;
}

export interface MediaConfig {
  /**
   * Repo path, e.g. `public/uploads` or `src/content/talks/images` (no leading slash). Empty: next to the entry
   * file (with `public: ""` the value is the bare file name), for collections with one folder per entry.
   */
  folder: string;
  /**
   * How a file in `folder` is written in content: an absolute public path (`/uploads`), or a path relative to the
   * entry file (`./images`) as Astro's `image()` expects.
   */
  public: string;
}

export interface UploadConfig {
  max_file_size?: number;
  /** Longest side in px for raster images; larger images are scaled down. */
  max_width?: number;
  max_height?: number;
  format?: 'webp' | 'keep';
  quality?: number;
  svg_optimize?: boolean;
}

export interface SlugConfig {
  encoding?: 'ascii' | 'unicode';
  clean_accents?: boolean;
  /** Max slug length; longer slugs are cut at a word boundary. */
  max_length?: number;
}

export interface I18nConfig {
  /** First is the main language. */
  locales: string[];
  default: string;
  /**
   * How translations are stored:
   *  - `field_suffix`: `title` + `title_en` in the same file (SMYK). Fields declare `translations: { en: title_en }`.
   *  - `multiple_files`: one file per locale; paths contain `{{locale}}` (Monte di Luce).
   */
  structure: 'field_suffix' | 'multiple_files';
  /** Missing translations show the default language on the site (informational, shown as a hint). */
  fallback?: boolean;
  /**
   * `side_by_side` (field_suffix only): no language tabs; every translatable field shows the main language and the
   * translation next to each other and both can be edited. Default `tabs`.
   */
  layout?: 'tabs' | 'side_by_side';
  /**
   * With `side_by_side`: when the main language text is typed (and the translation is empty or was itself machine
   * translated and not touched since), the CMS fills in a machine translation. Needs the Worker's AI binding.
   */
  auto_translate?: boolean;
}

export interface NavGroup {
  label: string;
  items: string[];
}

export interface StartScreen {
  /** Big buttons for the most common tasks. */
  shortcuts?: Shortcut[];
  /** Show the latest edits (default true). */
  recent?: boolean;
  /** Short welcome text under the site name. */
  intro?: string;
}

export interface Shortcut {
  label: string;
  /** `new` opens an empty entry, `edit` opens a singleton or an entry, `list` opens a collection. */
  action: 'new' | 'edit' | 'list' | 'media';
  /** Collection name, or `collection/file` for a singleton. */
  target?: string;
}

export type Collection = FolderCollection | FilesCollection;

interface CollectionBase {
  name: string;
  label: string;
  label_singular?: string;
  description?: string;
  media?: MediaConfig;
  /** Hide from navigation (still usable as a relation target). */
  hidden?: boolean;
}

export interface FolderCollection extends CollectionBase {
  type: 'folder';
  /** Repo folder; with multiple_files i18n it may contain `{{locale}}`. */
  folder: string;
  extension: 'md' | 'yaml' | 'yml' | 'json';
  format: 'frontmatter' | 'yaml' | 'json';
  create?: boolean;
  delete?: boolean;
  /** Template for new file names, e.g. `{{speaker}}`. */
  slug: string;
  /**
   * Where an entry lives inside `folder`, without the extension; default `{{slug}}`. `{{slug}}/index` makes each
   * entry a folder (`projects/simi/index.yaml`) that can hold its pictures too.
   */
  path?: string;
  /** Field shown as the entry title in lists. */
  title_field: string;
  /** Second line in lists, a template such as `{{title}}`. */
  summary?: string;
  /** Image fields tried in order for the list thumbnail (dotted paths for nested fields, e.g. `thumbnail.image`). */
  thumbnail?: string[];
  sort?: SortConfig;
  /**
   * A number field the client can drag-reorder. `desc` means a higher number shows first on the site
   * (SMYK products).
   */
  order_field?: { field: string; direction: 'asc' | 'desc' };
  /** A boolean field that hides the entry on the site (`draft: true` = hidden). */
  draft_field?: string;
  /** Path of the entry on the live site, e.g. `/zaznamy/{{slug}}`. */
  preview_path?: string;
  /**
   * `false`: the live preview doesn't scroll by itself, neither along with the form nor to the text being typed
   * (for a page that shows every entry on one long page). Default true.
   */
  preview_scroll?: boolean;
  fields: Field[];
}

export interface FilesCollection extends CollectionBase {
  type: 'files';
  files: SingletonFile[];
}

export interface SingletonFile {
  name: string;
  label: string;
  description?: string;
  /** Repo path; may contain `{{locale}}` with multiple_files i18n. */
  file: string;
  format?: 'frontmatter' | 'yaml' | 'json';
  media?: MediaConfig;
  preview_path?: string;
  /** `false`: the live preview doesn't scroll by itself (see the collection option). Default true. */
  preview_scroll?: boolean;
  fields: Field[];
}

export interface SortConfig {
  fields: string[];
  default: { field: string; direction: 'asc' | 'desc' };
}

export type FieldType =
  | 'string'
  | 'text'
  | 'markdown'
  | 'image'
  | 'gallery'
  | 'date'
  | 'datetime'
  | 'number'
  | 'boolean'
  | 'select'
  | 'relation'
  | 'list'
  | 'object'
  | 'url'
  | 'youtube'
  | 'color'
  | 'file'
  | 'hidden';

export interface FieldBase {
  name: string;
  label: string;
  type: FieldType;
  /** Default true, as in Sveltia. */
  required?: boolean;
  hint?: string;
  default?: unknown;
  /** Shown under a "More options" fold instead of in the main form. */
  advanced?: boolean;
  /** `[regex, message]` */
  pattern?: [string, string];
  /** With `field_suffix` i18n: the field holding each translation, e.g. `{ en: title_en }`. */
  translations?: Record<string, string>;
  /** With `multiple_files` i18n: whether the field differs per language (default true there). */
  i18n?: boolean;
}

export interface StringField extends FieldBase {
  type: 'string' | 'url' | 'youtube' | 'color';
  placeholder?: string;
  max_length?: number;
}
export interface TextField extends FieldBase {
  type: 'text';
  rows?: number;
  max_length?: number;
}
export interface MarkdownField extends FieldBase {
  type: 'markdown';
}
export interface ImageField extends FieldBase {
  type: 'image';
  media?: MediaConfig;
  accept?: string;
}
export interface GalleryField extends FieldBase {
  type: 'gallery';
  media?: MediaConfig;
  min?: number;
  max?: number;
  accept?: string;
  /**
   * Lets the gallery take videos too (a Dacid project's slider): a video is uploaded to R2 and its public URL is
   * stored in the list next to the picture paths, so photos and videos keep one order. Put video types in `accept`.
   */
  storage?: FileStorage;
}
/**
 * A file stored outside the repository, e.g. a video in a Cloudflare R2 bucket; the value is its public URL.
 * Without `storage` the editor can only paste a URL.
 */
export interface FileField extends FieldBase {
  type: 'file';
  accept?: string;
  storage?: FileStorage;
}

export interface FileStorage {
  type: 'r2';
  /** Bucket name; the CMS Worker needs a binding for it (see docs/CONFIG.md). */
  bucket: string;
  /** Public address of the bucket, e.g. https://media.hotpot-studio.com */
  public_url: string;
  /** Key prefix, e.g. `uploads/`. */
  prefix?: string;
  /** Bytes; default 2 GB. */
  max_file_size?: number;
}

export interface DateField extends FieldBase {
  type: 'date' | 'datetime';
  /** Stored format; dates default to `YYYY-MM-DD`. */
  format?: string;
}
export interface NumberField extends FieldBase {
  type: 'number';
  value_type?: 'int' | 'float';
  min?: number;
  max?: number;
  step?: number;
}
export interface BooleanField extends FieldBase {
  type: 'boolean';
}
export interface SelectField extends FieldBase {
  type: 'select';
  options: { label: string; value: string | number }[];
  multiple?: boolean;
  min?: number;
  max?: number;
}
export interface RelationField extends FieldBase {
  type: 'relation';
  /** A folder collection, or a singleton `collection/file`. */
  collection: string;
  /** For a singleton target: the list inside it that holds the options, e.g. `talkTags`. */
  list?: string;
  /** `slug` (entry file name) or a field name. */
  value: string;
  /** Field shown to the editor. */
  display: string;
  multiple?: boolean;
}
export interface ListField extends FieldBase {
  type: 'list';
  /** Item fields (a list of objects)… */
  fields?: Field[];
  /** …or one field (a list of plain values). */
  field?: Field;
  label_singular?: string;
  /** Item title template, e.g. `{{name}}`. */
  summary?: string;
  min?: number;
  max?: number;
  collapsed?: boolean;
  /** The file itself is this list (no key), e.g. SMYK's partners.yaml. */
  root?: boolean;
  /** Items of several kinds (page blocks): each item has `type_key` set to one of these names. */
  types?: ListType[];
  /** Default `type`. */
  type_key?: string;
  /** Items (with `fields`) of several kinds, chosen with a switch: see ObjectField.switch. */
  switch?: SwitchOption[];
}

export interface ListType {
  name: string;
  label: string;
  /** Item title template, e.g. `{{label}}`. */
  summary?: string;
  fields: Field[];
}
export interface ObjectField extends FieldBase {
  type: 'object';
  fields: Field[];
  collapsed?: boolean;
  /**
   * The object is one of several kinds, chosen with a switch (e.g. a picture, a video or a YouTube link). Each
   * option lists the fields it uses; the others are hidden and left out when saving. The kind shown for existing
   * content is the first option whose first field has a value (options whose first field no other option uses are
   * checked first: a video with a poster picture is a video).
   */
  switch?: SwitchOption[];
}

export interface SwitchOption {
  label: string;
  /** The fields this kind uses, in the order they are shown. */
  fields: string[];
  /** Other labels for fields under this kind, e.g. `{ image: Picture while the video loads }`. */
  labels?: Record<string, string>;
}

/** The fields that count for this data: with a switch, only the chosen option's (and those in no option). */
export function switchedFields(fields: Field[], options: SwitchOption[] | undefined, data: Record<string, unknown> | undefined): Field[] {
  if (!options) return fields;
  const chosen = new Set(options[switchOptionOf(options, data)]!.fields);
  const others = new Set(options.flatMap((o) => o.fields).filter((n) => !chosen.has(n)));
  return fields.filter((f) => !others.has(f.name));
}

/** Which option of a switch the data is (index), see ObjectField.switch. */
export function switchOptionOf(options: SwitchOption[], data: Record<string, unknown> | undefined): number {
  const filled = (name: string) => {
    const v = data?.[name];
    return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);
  };
  const shared = (name: string) => options.filter((o) => o.fields.includes(name)).length;
  const order = options.map((o, i) => i).sort((a, b) => shared(options[a]!.fields[0]!) - shared(options[b]!.fields[0]!));
  for (const i of order) if (options[i]!.fields[0] && filled(options[i]!.fields[0]!)) return i;
  return 0;
}
export interface HiddenField extends FieldBase {
  type: 'hidden';
}

export type Field =
  | StringField
  | TextField
  | MarkdownField
  | ImageField
  | FileField
  | GalleryField
  | DateField
  | NumberField
  | BooleanField
  | SelectField
  | RelationField
  | ListField
  | ObjectField
  | HiddenField;

// ------------------------------------------------------------------ helpers

export function isRequired(field: Field): boolean {
  return field.required !== false && field.type !== 'boolean' && field.type !== 'hidden';
}

/** Fields of one item of a list (for typed lists: of the item's type). */
export function listItemFields(field: ListField, item: unknown): Field[] | undefined {
  if (!field.types) return field.fields;
  const kind = item && typeof item === 'object' ? (item as Record<string, unknown>)[field.type_key ?? 'type'] : undefined;
  return field.types.find((t) => t.name === kind)?.fields;
}

export function getCollection(config: SiteConfig, name: string): Collection | undefined {
  return config.collections.find((c) => c.name === name);
}

/** Resolve a navigation item (`talks` or `settings/home`). */
export function resolveItem(
  config: SiteConfig,
  item: string,
): { collection: Collection; file?: SingletonFile } | undefined {
  const [name, fileName] = item.split('/');
  const collection = getCollection(config, name!);
  if (!collection) return undefined;
  if (fileName) {
    if (collection.type !== 'files') return undefined;
    const file = collection.files.find((f) => f.name === fileName);
    return file ? { collection, file } : undefined;
  }
  return { collection };
}

/** Navigation groups, with a sensible default when the config has none. */
export function navigationOf(config: SiteConfig): NavGroup[] {
  if (config.navigation?.length) return config.navigation;
  const settings = new Set(config.settings ?? []);
  const items = config.collections
    .filter((c) => !c.hidden)
    .flatMap((c) => (c.type === 'folder' ? [c.name] : c.files.map((f) => `${c.name}/${f.name}`)))
    .filter((i) => !settings.has(i));
  return [{ label: '', items }];
}

export function mediaFor(
  config: SiteConfig,
  collection: Collection | undefined,
  file?: SingletonFile,
  field?: { media?: MediaConfig },
): MediaConfig {
  return field?.media ?? file?.media ?? collection?.media ?? config.media;
}

export function localesOf(config: SiteConfig): string[] {
  return config.i18n?.locales ?? [];
}
